import { Contract } from '@ekt-group/general-purpose-api-interfaces';
import { ContractsSideloading } from '@ekt-group/general-purpose-api-interfaces/src/modules/contracts/types/contracts.enum';
import { useQueryClient } from '@tanstack/react-query';
import { Modal, Skeleton } from 'antd';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddAttorneyModal from '@/features/contracts/components/AddAttorneyModal';
import AttorneysModal from '@/features/contracts/components/AttorneysModal';
import { ContactInformationModal } from '@/features/contracts/components/ContactInformationModal';
import DeleteAttorneyModal from '@/features/contracts/components/DeleteAttorneyModal';
import EditAttorneyModal from '@/features/contracts/components/EditAttorneyModal';
import { CONTRACT_MODE, useContractModalStore } from '@/store/use-contract-modal-store';

import { useGetContracts } from '../api/use-get-contracts';

type ContractViewModalProps = {
  isModalOpen: boolean;
  handleClose: () => void;
  contractId: number;
  signDocumentLink?: string;
};

const SignDocumentFrame = ({ signDocumentLink }: { signDocumentLink: string }) => {
  if (!signDocumentLink) {
    return 'Something went wrong please try again';
  }

  return <iframe id="isign-gateway" width="100%" height="750px" src={signDocumentLink} title="iSign Gateway"></iframe>;
};

const ContractViewModal = ({ isModalOpen, handleClose, contractId, signDocumentLink }: ContractViewModalProps) => {
  const { t } = useTranslation('contractsTable');
  const queryClient = useQueryClient();
  const [contractPersonId, setContractPersonId] = useState<number>();

  const { mode } = useContractModalStore();

  const { data: contracts } = useGetContracts<Contract[]>({
    ids: [contractId],
    sideloading: [ContractsSideloading.Owner, ContractsSideloading.Kojv],
    disablePagination: true,
  });

  const contract = contracts ? contracts.find((contract) => contract.id === contractId) : null;

  if (!contract) {
    return null;
  }

  const modals: Record<CONTRACT_MODE, ReactNode> = {
    [CONTRACT_MODE.SIGN]: (
      <Modal
        width={600}
        centered
        styles={{
          body: {
            height: '80vh',
            overflowY: 'hidden',
          },
          content: {
            maxWidth: '100%',
          },
        }}
        footer={null}
        open={isModalOpen}
        cancelButtonProps={{ hidden: true }}
        onCancel={async () => {
          await queryClient.invalidateQueries({
            predicate: (query) => query.queryKey.includes('contracts'),
          });
          handleClose();
        }}
        title={<h2 className="font-bold text-2xl">{`${t('signContract')} - L${contract?.contractNumber}`}</h2>}
      >
        {signDocumentLink ? <SignDocumentFrame signDocumentLink={signDocumentLink} /> : <Skeleton.Node className="w-full h-full" active />}
      </Modal>
    ),

    [CONTRACT_MODE.VIEW]: <ContactInformationModal isModalOpen={isModalOpen} contract={contract} handleClose={handleClose} />,

    [CONTRACT_MODE.MANAGE_ATTORNEYS]: (
      <AttorneysModal
        isModalOpen={isModalOpen}
        handleClose={handleClose}
        contractId={contractId}
        contractNumber={contract?.contractNumber}
        setContractPersonId={setContractPersonId}
      />
    ),

    [CONTRACT_MODE.ADD_ATTORNEY]: (
      <AddAttorneyModal isModalOpen={isModalOpen} contractId={contractId} contractNumber={contract.contractNumber} />
    ),

    [CONTRACT_MODE.EDIT_ATTORNEY]: (
      <EditAttorneyModal contractPersonId={contractPersonId} isModalOpen={isModalOpen} contractNumber={contract.contractNumber} />
    ),

    [CONTRACT_MODE.DELETE_ATTORNEY]: <DeleteAttorneyModal contractPersonId={contractPersonId} isModalOpen={isModalOpen} />,
  };

  return modals[mode];
};

export default ContractViewModal;
